import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Film, Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      toast.success('Password reset! You can now sign in.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#060608] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#FF4500] flex items-center justify-center"><Film className="w-5 h-5 text-white" /></div>
          <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>AvantGarde Media</span>
        </div>
        <div className="bg-[#0d0d11] border border-white/10 rounded-2xl p-8">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <p className="text-white font-semibold mb-1">Password updated</p>
              <p className="text-sm text-[#8A8A93] mb-5">Your password has been reset successfully.</p>
              <button onClick={() => navigate('/login')} data-testid="reset-goto-login"
                className="ff-btn-primary w-full">Go to sign in</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose a new password</h1>
                <p className="text-sm text-[#8A8A93]">Enter and confirm your new password below.</p>
              </div>
              <div className="relative">
                <input data-testid="reset-password-input" type={show ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="New password (6+ chars)" className="ff-input w-full pr-12" required minLength={6} />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#55555C] hover:text-white">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <input data-testid="reset-confirm-input" type={show ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Confirm new password" className="ff-input w-full" required minLength={6} />
              <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                className="ff-btn-primary w-full flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Reset password
              </button>
              <button type="button" onClick={() => navigate('/login')} className="w-full text-center text-xs text-[#8A8A93] hover:text-white transition-colors">
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
